import { collectionMethods } from './collectionMethods.js';

const BASE_PATH = '/auction-operations';

/**
 * Auction Operations — the brand an auction runs under, and the Companies
 * standing behind it.
 *
 * It carries the Operation-wide knockout lists. Both are read live from this
 * document on every check and never from a house's copy, which is why they are
 * set here and nowhere else.
 *
 * Every route below writes its own path out in full. Several of them appear on
 * other nouns word for word, and that repetition is deliberate: a reader asking
 * what `setLegalCompany` reaches should find the answer here rather than in a
 * factory that assembles it from a base path passed in from somewhere else.
 */
export function auctionOperationMethods({ sendRequest, buildUrl }) {
    return {
        ...collectionMethods({ basePath: BASE_PATH, sendRequest, buildUrl }),

        /**
         * The Company this Operation legally is.
         *
         * @param {string|null} companyId - `null` clears it.
         */
        async setLegalCompany(id, companyId) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/legal-company`),
                'PUT',
                { companyId }
            );
        },

        /**
         * The outside Company managing this Operation.
         *
         * Manager and owner are two Companies on one document.
         *
         * @param {string|null} companyId - `null` clears it.
         */
        async setManagementCompany(id, companyId) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/management-company`),
                'PUT',
                { companyId }
            );
        },

        /**
         * Who pays, and under which Stripe subscription.
         *
         * The server takes one of two bodies here and nothing between them:
         * `{ companyId, stripeSubscriptionId }` to name a payer, or
         * `{ companyId: null }` alone to clear. So a clear sends one key rather
         * than two, and a caller that passes a subscription with a null payer
         * gets the refusal it earned rather than a silently dropped field.
         *
         * @param {string|null} companyId
         * @param {string} [stripeSubscriptionId] - Required whenever a payer is named.
         */
        async setBillingCompany(id, companyId, stripeSubscriptionId) {
            const body = { companyId };
            if (stripeSubscriptionId !== undefined) {
                body.stripeSubscriptionId = stripeSubscriptionId;
            }
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/billing-company`),
                'PUT',
                body
            );
        },

        /**
         * Set one policy. The shape is the policy's own — the server stores it
         * as given and the application validates it against the key.
         */
        async setPolicy(id, policyName, policy) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/policies/${encodeURIComponent(policyName)}`),
                'PUT',
                policy
            );
        },

        /** Remove one policy. */
        async clearPolicy(id, policyName) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/policies/${encodeURIComponent(policyName)}`),
                'DELETE'
            );
        },

        /**
         * Replace the human knockout list whole.
         *
         * A replace rather than an add and a remove, because that is what the
         * route is: the server takes the list it is given and the list it is
         * given is the list. A caller adding one entry sends the others back.
         *
         * @param {Array<{ humanId: string, name: string }>} knockedOutHumans
         */
        async setKnockedOutHumans(id, knockedOutHumans) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/knocked-out-humans`),
                'PUT',
                { knockedOutHumans }
            );
        },

        /**
         * Bar one Dealership from every house this Operation manages.
         *
         * A member of a set addressed entirely in the path, so there is nothing
         * to send. The list is not replaced the way the human knockout list is,
         * because a dealership is named by identifier and a human entry carries
         * a denormalized name beside it.
         */
        async knockOutDealership(id, dealershipId) {
            return await sendRequest(
                buildUrl(
                    `${BASE_PATH}/${encodeURIComponent(id)}/knocked-out-dealerships/${encodeURIComponent(dealershipId)}`
                ),
                'PUT'
            );
        },

        /** Lift that bar. */
        async restoreDealership(id, dealershipId) {
            return await sendRequest(
                buildUrl(
                    `${BASE_PATH}/${encodeURIComponent(id)}/knocked-out-dealerships/${encodeURIComponent(dealershipId)}`
                ),
                'DELETE'
            );
        },

        /**
         * A page of the Auction Houses this Operation manages.
         *
         * Answers the same `{ meta, documents }` page the list and search routes
         * answer, and pages the same way.
         */
        async listAuctionHouses(id, { page, pageSize } = {}) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/auction-houses`, { page, pageSize }),
                'GET'
            );
        }
    };
}

export default auctionOperationMethods;
